import React, { useState, useEffect } from "react";
import { db } from "../../services/firebase";
import {
	collection,
	onSnapshot,
	doc,
	updateDoc,
	deleteDoc,
	addDoc,
} from "firebase/firestore";
import {
	Plus,
	Trash2,
	Edit3,
	Check,
	X,
	ArrowRight,
	Download,
	ChevronDown,
	ChevronUp,
} from "lucide-react";

const Pipeline = ({ loading: parentLoading, onMoveToUsers }) => {
	const [clients, setClients] = useState([]);
	const [loading, setLoading] = useState(true);
	const [showAddForm, setShowAddForm] = useState(false);
	const [editingId, setEditingId] = useState(null);
	const [editData, setEditData] = useState({});
	const [expandedCards, setExpandedCards] = useState({});
	const [newClient, setNewClient] = useState({
		clientName: "",
		sessionPlan: "",
		startDate: "",
		cardDetails: false,
		healthDetails: false,
		welcomeEmail: false,
		addedToGroupChat: false,
		followUp24h: false,
		followUp2Weeks: false,
		followUp1Month: false,
		cancelled: false,
		notes: "",
	});

	useEffect(() => {
		const pipelineRef = collection(db, "pipeline");
		const unsubscribe = onSnapshot(
			pipelineRef,
			(snapshot) => {
				const data = snapshot.docs.map((d) => ({
					id: d.id,
					...d.data(),
				}));
				// Sort client-side by createdAt descending
				data.sort((a, b) =>
					(b.createdAt || "").localeCompare(a.createdAt || ""),
				);
				setClients(data);
				setLoading(false);
			},
			(error) => {
				console.error("Pipeline listener error:", error);
				setClients([]);
				setLoading(false);
			},
		);
		return () => unsubscribe();
	}, []);

	const handleAddClient = async (e) => {
		e.preventDefault();
		if (!newClient.clientName.trim()) return;
		try {
			await addDoc(collection(db, "pipeline"), {
				...newClient,
				createdAt: new Date().toISOString(),
			});
			setNewClient({
				clientName: "",
				sessionPlan: "",
				startDate: "",
				cardDetails: false,
				healthDetails: false,
				welcomeEmail: false,
				addedToGroupChat: false,
				followUp24h: false,
				followUp2Weeks: false,
				followUp1Month: false,
				cancelled: false,
				notes: "",
			});
			setShowAddForm(false);
		} catch (error) {
			console.error("Error adding pipeline client:", error);
		}
	};

	const startEditing = (client) => {
		setEditingId(client.id);
		setEditData({ ...client });
	};

	const cancelEditing = () => {
		setEditingId(null);
		setEditData({});
	};

	const saveEdit = async () => {
		if (!editingId) return;
		try {
			const { id, ...dataToSave } = editData;
			await updateDoc(doc(db, "pipeline", editingId), dataToSave);
			setEditingId(null);
			setEditData({});
		} catch (error) {
			console.error("Error updating pipeline client:", error);
		}
	};

	const deleteClient = async (id) => {
		if (!window.confirm("Remove this client from the pipeline?")) return;
		try {
			await deleteDoc(doc(db, "pipeline", id));
		} catch (error) {
			console.error("Error deleting pipeline client:", error);
		}
	};

	const handleMoveToUsers = (client) => {
		if (
			window.confirm(
				`Move ${client.clientName} to User Management? This will remove them from the pipeline.`,
			)
		) {
			if (onMoveToUsers) {
				onMoveToUsers(client);
			}
			deleteDoc(doc(db, "pipeline", client.id)).catch(console.error);
		}
	};

	const toggleCard = (id) => {
		setExpandedCards((prev) => ({ ...prev, [id]: !prev[id] }));
	};

	const exportPipelineCSV = () => {
		const escapeCSV = (val) => {
			const str = String(val ?? "");
			return str.includes(",") || str.includes('"') || str.includes("\n")
				? `"${str.replace(/"/g, '""')}"`
				: str;
		};
		const headers = [
			"Client Name",
			"Session Plan",
			"Start Date",
			"Card Details",
			"Health Details",
			"Welcome Email",
			"Added to Group Chat",
			"Follow Up 24h",
			"Follow Up 2 Weeks",
			"Follow Up 1 Month",
			"Cancelled",
			"Notes",
		];
		const rows = clients.map((c) => [
			c.clientName,
			c.sessionPlan,
			c.startDate,
			c.cardDetails ? "Yes" : "No",
			c.healthDetails ? "Yes" : "No",
			c.welcomeEmail ? "Yes" : "No",
			c.addedToGroupChat ? "Yes" : "No",
			c.followUp24h ? "Yes" : "No",
			c.followUp2Weeks ? "Yes" : "No",
			c.followUp1Month ? "Yes" : "No",
			c.cancelled ? "Yes" : "No",
			c.notes || "",
		]);
		const csv = [headers, ...rows]
			.map((r) => r.map(escapeCSV).join(","))
			.join("\n");
		const blob = new Blob([csv], { type: "text/csv" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `pipeline-${new Date().toISOString().slice(0, 10)}.csv`;
		a.click();
		URL.revokeObjectURL(url);
	};

	const activeClients = clients.filter((c) => !c.cancelled);
	const cancelledClients = clients.filter((c) => c.cancelled);

	if (loading || parentLoading) {
		return (
			<div className="text-center py-5">
				<div className="spinner-border text-primary" role="status">
					<span className="visually-hidden">Loading...</span>
				</div>
			</div>
		);
	}

	const CheckboxCell = ({ checked, onChange, label }) => (
		<div style={{ display: "flex", justifyContent: "center" }}>
			<input
				type="checkbox"
				checked={checked || false}
				onChange={onChange}
				aria-label={label}
				style={{
					accentColor: "#d15a36",
					width: "18px",
					height: "18px",
					cursor: "pointer",
				}}
			/>
		</div>
	);

	return (
		<div>
			{/* Header */}
			<div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
				<div className="d-flex align-items-center gap-2">
					<span className="badge bg-primary">
						{activeClients.length} active
					</span>
					{cancelledClients.length > 0 && (
						<span className="badge bg-secondary">
							{cancelledClients.length} cancelled
						</span>
					)}
				</div>
				<div className="d-flex gap-2">
					<button
						className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
						onClick={exportPipelineCSV}
						disabled={clients.length === 0}
					>
						<Download size={16} />
						Export
					</button>
					<button
						className="btn btn-primary btn-sm d-flex align-items-center gap-1"
						onClick={() => setShowAddForm(!showAddForm)}
					>
						<Plus size={16} />
						Add Client
					</button>
				</div>
			</div>

			{/* Add Client Form */}
			{showAddForm && (
				<div className="mb-4 p-3 bg-light rounded">
					<h6>Add New Client to Pipeline</h6>
					<form onSubmit={handleAddClient}>
						<div className="row g-3">
							<div className="col-12 col-md-4">
								<label className="form-label">Client Name *</label>
								<input
									type="text"
									className="form-control"
									value={newClient.clientName}
									onChange={(e) =>
										setNewClient({ ...newClient, clientName: e.target.value })
									}
									placeholder="Enter client name"
									required
								/>
							</div>
							<div className="col-12 col-md-4">
								<label className="form-label">Session Plan</label>
								<input
									type="text"
									className="form-control"
									value={newClient.sessionPlan}
									onChange={(e) =>
										setNewClient({ ...newClient, sessionPlan: e.target.value })
									}
									placeholder="e.g. 3x per week"
								/>
							</div>
							<div className="col-12 col-md-4">
								<label className="form-label">Start Date</label>
								<input
									type="date"
									className="form-control"
									value={newClient.startDate}
									onChange={(e) =>
										setNewClient({ ...newClient, startDate: e.target.value })
									}
								/>
							</div>
							{/* Checkboxes */}
							<div className="col-12">
								<label className="form-label d-block mb-2">
									Onboarding Checklist
								</label>
								<div className="row g-2">
									{[
										{ key: "cardDetails", label: "Card details entered" },
										{ key: "healthDetails", label: "Health details entered" },
										{ key: "welcomeEmail", label: "Welcome email sent" },
										{ key: "addedToGroupChat", label: "Added to group chat" },
									].map((item) => (
										<div key={item.key} className="col-12 col-sm-6 col-md-3">
											<label
												htmlFor={`new-${item.key}`}
												className="d-flex align-items-center gap-2 p-2 rounded"
												style={{
													backgroundColor: "#1a1a1a",
													border: newClient[item.key]
														? "1px solid #d15a36"
														: "1px solid #333",
													cursor: "pointer",
													transition: "all 0.2s ease",
												}}
											>
												<input
													type="checkbox"
													checked={newClient[item.key]}
													onChange={(e) =>
														setNewClient({
															...newClient,
															[item.key]: e.target.checked,
														})
													}
													id={`new-${item.key}`}
													style={{
														accentColor: "#d15a36",
														width: "18px",
														height: "18px",
														flexShrink: 0,
													}}
												/>
												<span
													style={{
														color: newClient[item.key] ? "#d15a36" : "#fff",
														fontSize: "0.85rem",
													}}
												>
													{item.label}
												</span>
											</label>
										</div>
									))}
								</div>
							</div>
							<div className="col-12">
								<label className="form-label d-block mb-2">Follow Ups</label>
								<div className="row g-2">
									{[
										{
											key: "followUp24h",
											label: "After first session (within 24h)",
										},
										{
											key: "followUp2Weeks",
											label: "2 weeks after first session",
										},
										{
											key: "followUp1Month",
											label: "1 month after first session",
										},
									].map((item) => (
										<div key={item.key} className="col-12 col-sm-6 col-md-4">
											<label
												htmlFor={`new-${item.key}`}
												className="d-flex align-items-center gap-2 p-2 rounded"
												style={{
													backgroundColor: "#1a1a1a",
													border: newClient[item.key]
														? "1px solid #d15a36"
														: "1px solid #333",
													cursor: "pointer",
													transition: "all 0.2s ease",
												}}
											>
												<input
													type="checkbox"
													checked={newClient[item.key]}
													onChange={(e) =>
														setNewClient({
															...newClient,
															[item.key]: e.target.checked,
														})
													}
													id={`new-${item.key}`}
													style={{
														accentColor: "#d15a36",
														width: "18px",
														height: "18px",
														flexShrink: 0,
													}}
												/>
												<span
													style={{
														color: newClient[item.key] ? "#d15a36" : "#fff",
														fontSize: "0.85rem",
													}}
												>
													{item.label}
												</span>
											</label>
										</div>
									))}
								</div>
							</div>
							<div className="col-12">
								<label className="form-label">Notes</label>
								<textarea
									className="form-control"
									rows="2"
									value={newClient.notes}
									onChange={(e) =>
										setNewClient({ ...newClient, notes: e.target.value })
									}
									placeholder="Any additional notes..."
								/>
							</div>
							<div className="col-12">
								<div className="d-flex gap-2">
									<button type="submit" className="btn btn-success btn-sm">
										Add to Pipeline
									</button>
									<button
										type="button"
										className="btn btn-secondary btn-sm"
										onClick={() => setShowAddForm(false)}
									>
										Cancel
									</button>
								</div>
							</div>
						</div>
					</form>
				</div>
			)}

			{clients.length === 0 ? (
				<div className="text-center py-5">
					<p className="text-muted">
						No clients in the pipeline yet. Add a client or move one from
						Enquiries.
					</p>
				</div>
			) : (
				<>
					{/* Desktop Table View */}
					<div className="table-responsive d-none d-lg-block">
						<table
							className="table table-hover align-middle"
							style={{ fontSize: "0.85rem" }}
						>
							<thead>
								<tr>
									<th title="Client's full name">Client Name</th>
									<th title="Number of sessions per week agreed">
										Session Plan
									</th>
									<th title="Date the client starts training">Start Date</th>
									<th
										className="text-center"
										title="Payment card details have been entered"
										style={{ cursor: "help" }}
									>
										Card
									</th>
									<th
										className="text-center"
										title="Health questionnaire / PAR-Q completed"
										style={{ cursor: "help" }}
									>
										Health
									</th>
									<th
										className="text-center"
										title="Welcome email has been sent to the client"
										style={{ cursor: "help" }}
									>
										Welcome
									</th>
									<th
										className="text-center"
										title="Client has been added to the group chat"
										style={{ cursor: "help" }}
									>
										Group
									</th>
									<th
										className="text-center"
										title="Follow up within 24 hours after first session"
										style={{ cursor: "help" }}
									>
										24h
									</th>
									<th
										className="text-center"
										title="Follow up check-in 2 weeks after first session"
										style={{ cursor: "help" }}
									>
										2wk
									</th>
									<th
										className="text-center"
										title="Follow up check-in 1 month after first session"
										style={{ cursor: "help" }}
									>
										1mo
									</th>
									<th
										className="text-center"
										title="Client has cancelled their membership"
										style={{ cursor: "help" }}
									>
										Cancel
									</th>
									<th>Actions</th>
								</tr>
							</thead>
							<tbody>
								{clients.map((client) => {
									const isEditing = editingId === client.id;
									if (isEditing) {
										return (
											<tr key={client.id} className="table-active">
												<td>
													<input
														type="text"
														className="form-control form-control-sm"
														value={editData.clientName || ""}
														onChange={(e) =>
															setEditData({
																...editData,
																clientName: e.target.value,
															})
														}
													/>
												</td>
												<td>
													<input
														type="text"
														className="form-control form-control-sm"
														value={editData.sessionPlan || ""}
														onChange={(e) =>
															setEditData({
																...editData,
																sessionPlan: e.target.value,
															})
														}
													/>
												</td>
												<td>
													<input
														type="date"
														className="form-control form-control-sm"
														value={editData.startDate || ""}
														onChange={(e) =>
															setEditData({
																...editData,
																startDate: e.target.value,
															})
														}
													/>
												</td>
												<td className="text-center">
													<CheckboxCell
														checked={editData.cardDetails}
														onChange={(e) =>
															setEditData({
																...editData,
																cardDetails: e.target.checked,
															})
														}
														label="Card details"
													/>
												</td>
												<td className="text-center">
													<CheckboxCell
														checked={editData.healthDetails}
														onChange={(e) =>
															setEditData({
																...editData,
																healthDetails: e.target.checked,
															})
														}
														label="Health details"
													/>
												</td>
												<td className="text-center">
													<CheckboxCell
														checked={editData.welcomeEmail}
														onChange={(e) =>
															setEditData({
																...editData,
																welcomeEmail: e.target.checked,
															})
														}
														label="Welcome email"
													/>
												</td>
												<td className="text-center">
													<CheckboxCell
														checked={editData.addedToGroupChat}
														onChange={(e) =>
															setEditData({
																...editData,
																addedToGroupChat: e.target.checked,
															})
														}
														label="Group chat"
													/>
												</td>
												<td className="text-center">
													<CheckboxCell
														checked={editData.followUp24h}
														onChange={(e) =>
															setEditData({
																...editData,
																followUp24h: e.target.checked,
															})
														}
														label="24h follow up"
													/>
												</td>
												<td className="text-center">
													<CheckboxCell
														checked={editData.followUp2Weeks}
														onChange={(e) =>
															setEditData({
																...editData,
																followUp2Weeks: e.target.checked,
															})
														}
														label="2 week follow up"
													/>
												</td>
												<td className="text-center">
													<CheckboxCell
														checked={editData.followUp1Month}
														onChange={(e) =>
															setEditData({
																...editData,
																followUp1Month: e.target.checked,
															})
														}
														label="1 month follow up"
													/>
												</td>
												<td className="text-center">
													<CheckboxCell
														checked={editData.cancelled}
														onChange={(e) =>
															setEditData({
																...editData,
																cancelled: e.target.checked,
															})
														}
														label="Cancelled"
													/>
												</td>
												<td>
													<div className="btn-group btn-group-sm">
														<button
															className="btn btn-success"
															onClick={saveEdit}
															title="Save"
														>
															<Check size={14} />
														</button>
														<button
															className="btn btn-secondary"
															onClick={cancelEditing}
															title="Cancel"
														>
															<X size={14} />
														</button>
													</div>
												</td>
											</tr>
										);
									}

									return (
										<tr
											key={client.id}
											style={{ opacity: client.cancelled ? 0.5 : 1 }}
										>
											<td>
												<strong>{client.clientName}</strong>
												{client.notes && (
													<small
														className="d-block text-muted"
														style={{
															maxWidth: 150,
															whiteSpace: "nowrap",
															overflow: "hidden",
															textOverflow: "ellipsis",
														}}
													>
														{client.notes}
													</small>
												)}
											</td>
											<td>{client.sessionPlan || "—"}</td>
											<td>{client.startDate || "—"}</td>
											<td className="text-center">
												{client.cardDetails ? "✓" : "—"}
											</td>
											<td className="text-center">
												{client.healthDetails ? "✓" : "—"}
											</td>
											<td className="text-center">
												{client.welcomeEmail ? "✓" : "—"}
											</td>
											<td className="text-center">
												{client.addedToGroupChat ? "✓" : "—"}
											</td>
											<td className="text-center">
												{client.followUp24h ? "✓" : "—"}
											</td>
											<td className="text-center">
												{client.followUp2Weeks ? "✓" : "—"}
											</td>
											<td className="text-center">
												{client.followUp1Month ? "✓" : "—"}
											</td>
											<td className="text-center">
												{client.cancelled ? (
													<span
														className="badge bg-danger"
														style={{ fontSize: "0.7rem" }}
													>
														Yes
													</span>
												) : (
													"—"
												)}
											</td>
											<td>
												<div className="btn-group btn-group-sm">
													<button
														className="btn btn-outline-primary"
														onClick={() => startEditing(client)}
														title="Edit"
													>
														<Edit3 size={14} />
													</button>
													{!client.cancelled && (
														<button
															className="btn btn-outline-success"
															onClick={() => handleMoveToUsers(client)}
															title="Move to User Management"
														>
															<ArrowRight size={14} />
														</button>
													)}
													<button
														className="btn btn-outline-danger"
														onClick={() => deleteClient(client.id)}
														title="Remove"
													>
														<Trash2 size={14} />
													</button>
												</div>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>

					{/* Mobile Card View */}
					<div className="d-lg-none">
						{clients.map((client) => {
							const isExpanded = expandedCards[client.id];
							const isEditing = editingId === client.id;

							return (
								<div
									key={client.id}
									className="mb-3 p-3"
									style={{
										backgroundColor: "#1a1a1a",
										borderRadius: "8px",
										border: client.cancelled
											? "1px solid #dc3545"
											: "1px solid #333",
										opacity: client.cancelled ? 0.6 : 1,
									}}
								>
									{/* Card Header */}
									<div
										className="d-flex justify-content-between align-items-start"
										onClick={() => !isEditing && toggleCard(client.id)}
										style={{ cursor: isEditing ? "default" : "pointer" }}
									>
										<div>
											<strong style={{ color: "#fff" }}>
												{client.clientName}
											</strong>
											{client.sessionPlan && (
												<small
													className="d-block"
													style={{ color: "rgba(255,255,255,0.5)" }}
												>
													{client.sessionPlan}
												</small>
											)}
											{client.startDate && (
												<small
													className="d-block"
													style={{ color: "rgba(255,255,255,0.4)" }}
												>
													Start: {client.startDate}
												</small>
											)}
										</div>
										<div className="d-flex align-items-center gap-2">
											{client.cancelled && (
												<span
													className="badge bg-danger"
													style={{ fontSize: "0.7rem" }}
												>
													Cancelled
												</span>
											)}
											{isExpanded ? (
												<ChevronUp size={18} style={{ color: "#d15a36" }} />
											) : (
												<ChevronDown
													size={18}
													style={{ color: "rgba(255,255,255,0.5)" }}
												/>
											)}
										</div>
									</div>

									{/* Expanded Content */}
									{isExpanded && (
										<div
											className="mt-3 pt-3"
											style={{ borderTop: "1px solid #333" }}
										>
											{isEditing ? (
												<div>
													<div className="row g-2 mb-3">
														<div className="col-12">
															<input
																type="text"
																className="form-control form-control-sm"
																value={editData.clientName || ""}
																onChange={(e) =>
																	setEditData({
																		...editData,
																		clientName: e.target.value,
																	})
																}
																placeholder="Client Name"
															/>
														</div>
														<div className="col-6">
															<input
																type="text"
																className="form-control form-control-sm"
																value={editData.sessionPlan || ""}
																onChange={(e) =>
																	setEditData({
																		...editData,
																		sessionPlan: e.target.value,
																	})
																}
																placeholder="Session Plan"
															/>
														</div>
														<div className="col-6">
															<input
																type="date"
																className="form-control form-control-sm"
																value={editData.startDate || ""}
																onChange={(e) =>
																	setEditData({
																		...editData,
																		startDate: e.target.value,
																	})
																}
															/>
														</div>
													</div>
													<div className="row g-2 mb-3">
														{[
															{ key: "cardDetails", label: "Card Details" },
															{ key: "healthDetails", label: "Health Details" },
															{ key: "welcomeEmail", label: "Welcome Email" },
															{ key: "addedToGroupChat", label: "Group Chat" },
															{ key: "followUp24h", label: "Follow Up 24h" },
															{ key: "followUp2Weeks", label: "Follow Up 2wk" },
															{ key: "followUp1Month", label: "Follow Up 1mo" },
															{ key: "cancelled", label: "Cancelled" },
														].map((item) => (
															<div key={item.key} className="col-12">
																<label
																	htmlFor={`edit-${client.id}-${item.key}`}
																	className="d-flex align-items-center gap-2 p-2 rounded"
																	style={{
																		backgroundColor: "#111",
																		border: editData[item.key]
																			? "1px solid #d15a36"
																			: "1px solid #333",
																		cursor: "pointer",
																		transition: "all 0.2s ease",
																	}}
																>
																	<input
																		type="checkbox"
																		checked={editData[item.key] || false}
																		onChange={(e) =>
																			setEditData({
																				...editData,
																				[item.key]: e.target.checked,
																			})
																		}
																		id={`edit-${client.id}-${item.key}`}
																		style={{
																			accentColor: "#d15a36",
																			width: "16px",
																			height: "16px",
																			flexShrink: 0,
																		}}
																	/>
																	<span
																		style={{
																			color: editData[item.key]
																				? "#d15a36"
																				: "rgba(255,255,255,0.7)",
																			fontSize: "0.8rem",
																		}}
																	>
																		{item.label}
																	</span>
																</label>
															</div>
														))}
													</div>
													<div className="mb-3">
														<textarea
															className="form-control form-control-sm"
															rows="2"
															value={editData.notes || ""}
															onChange={(e) =>
																setEditData({
																	...editData,
																	notes: e.target.value,
																})
															}
															placeholder="Notes..."
														/>
													</div>
													<div className="d-flex gap-2">
														<button
															className="btn btn-success btn-sm"
															onClick={saveEdit}
														>
															<Check size={14} className="me-1" />
															Save
														</button>
														<button
															className="btn btn-secondary btn-sm"
															onClick={cancelEditing}
														>
															Cancel
														</button>
													</div>
												</div>
											) : (
												<div>
													{/* Progress Checklist */}
													<div className="row g-2 mb-3">
														{[
															{ key: "cardDetails", label: "Card Details" },
															{ key: "healthDetails", label: "Health Details" },
															{ key: "welcomeEmail", label: "Welcome Email" },
															{ key: "addedToGroupChat", label: "Group Chat" },
															{ key: "followUp24h", label: "Follow Up (24h)" },
															{
																key: "followUp2Weeks",
																label: "Follow Up (2wk)",
															},
															{
																key: "followUp1Month",
																label: "Follow Up (1mo)",
															},
														].map((item) => (
															<div key={item.key} className="col-12">
																<div
																	className="d-flex align-items-center gap-2 p-2 rounded"
																	style={{
																		backgroundColor: "#111",
																		border: client[item.key]
																			? "1px solid #d15a36"
																			: "1px solid #333",
																	}}
																>
																	<span
																		style={{
																			color: client[item.key]
																				? "#d15a36"
																				: "rgba(255,255,255,0.4)",
																			fontSize: "0.8rem",
																		}}
																	>
																		{client[item.key] ? "✓" : "○"} {item.label}
																	</span>
																</div>
															</div>
														))}
													</div>
													{client.notes && (
														<p
															className="mb-3"
															style={{
																color: "rgba(255,255,255,0.6)",
																fontSize: "0.85rem",
															}}
														>
															{client.notes}
														</p>
													)}
													{/* Actions */}
													<div className="d-flex flex-wrap gap-2">
														<button
															className="btn btn-sm"
															onClick={() => startEditing(client)}
															style={{
																backgroundColor: "rgba(255,255,255,0.1)",
																color: "#fff",
																border: "none",
															}}
														>
															<Edit3 size={14} className="me-1" />
															Edit
														</button>
														{!client.cancelled && (
															<button
																className="btn btn-sm"
																onClick={() => handleMoveToUsers(client)}
																style={{
																	backgroundColor: "rgba(25,135,84,0.15)",
																	color: "#198754",
																	border: "none",
																}}
															>
																<ArrowRight size={14} className="me-1" />
																Move to Users
															</button>
														)}
														<button
															className="btn btn-sm"
															onClick={() => deleteClient(client.id)}
															style={{
																backgroundColor: "rgba(220,53,69,0.15)",
																color: "#dc3545",
																border: "none",
															}}
														>
															<Trash2 size={14} className="me-1" />
															Remove
														</button>
													</div>
												</div>
											)}
										</div>
									)}
								</div>
							);
						})}
					</div>
				</>
			)}
		</div>
	);
};

export default Pipeline;
